/**
 * The Open ▾ menu — ADR-032 §2.4's SECOND GESTURE on the one load door.
 *
 * Drag-and-drop was the app's only way to get a file in, and Allen asked for
 * a menu "if it helps with these foundations". It does, for one reason: the
 * e2e smoke and a person now walk the SAME code — a chosen File is handed to
 * the very functions a drop reaches, so there is one door with two handles,
 * never two doors. Five entries, in the order D4 ruled: Open character · Add
 * animation · Open Document · Save Document · Recent. Plus, while a Document
 * waits for the files it names (D6), a section that offers each relink.
 *
 * DOM ONLY; DECIDES NOTHING (the undo-chrome shape, ADR-006 §13.0.1). What a
 * File means, whether a Document is valid, what "recent" is — all main's, all
 * reached through `deps`. This file knows how to ask the browser for a file
 * (`showOpenFilePicker` where present, because it yields a handle Recent can
 * keep; an `<input type=file>` otherwise) and how to render a list.
 *
 * THE LOAD ORDER IS RESERVED WHEN THE CHOICE IS FINAL. The moment a dialog is
 * confirmed or a remembered handle granted — and BEFORE the file is read — the
 * menu takes a ticket (`reserveLoad`) and hands it on with the file. A read
 * can be slow (a network drive, a large GLB), and a later choice must not
 * overtake an earlier one because its bytes arrived first (Codex P1, #140
 * round 2; the door's stale-load guard compares against this number).
 *
 * NO DEAD ENTRIES. An entry with nothing to act on is disabled WITH ITS
 * REASON rendered under it (ADR-032 §7; the spec's no-`title=` rule §13.3),
 * never hidden. Mounted into the shell chrome bar the export button already
 * joined, so it can never sit over a workspace zone.
 *
 * Depends on: tooltip (tier-1 tip), session types. Depended on by: main.
 */
import type { MissingUpload, PendingOpen } from '../session/document-io.js';
import type { FileHandleLike, RememberedFile } from '../session/file-handles.js';
import { attachTip } from './tooltip.js';

export type MenuFileKind = 'character' | 'animation' | 'document';

export interface FileMenuState {
  /** Is there a subject an animation could be added to? */
  readonly hasSubject: boolean;
  /** Whether Recent can work at all — and if not, the reason it renders. */
  readonly recent:
    { readonly available: true } | { readonly available: false; readonly reason: string };
  /** A Document waiting for the files it names, or null. */
  readonly waiting: PendingOpen | null;
}

export interface FileMenuDeps {
  /**
   * A ticket for the load order, taken the moment a choice is FINAL (the
   * dialog confirmed, the handle granted) and before any read. Handed on to
   * `opened` unchanged.
   */
  readonly reserveLoad: () => number;
  /** Is a ticket still the latest gesture? A late failure under a stale ticket keeps its notice to the console. */
  readonly isCurrent: (seq: number) => boolean;
  /** A file was chosen for a kind — with its handle when the browser gave one — under `seq`. main routes it. */
  readonly opened: (
    kind: MenuFileKind,
    file: File,
    handle: FileHandleLike | null,
    seq: number,
    /** For a RELINK: the wait's identity (`PendingOpen.id`) — the door refuses the file if that wait has ended. */
    relinkOf?: string,
  ) => Promise<boolean>;
  readonly saveDocument: () => Promise<void>;
  /**
   * A remembered handle the browser GRANTS right now, or null. It may ask — a
   * click is behind it. The read is the menu's, after it reserves the order.
   */
  readonly grantedHandle: (uri: string) => Promise<FileHandleLike | null>;
  readonly recent: () => Promise<readonly RememberedFile[]>;
  /** Asked when the menu opens — enablement and the relink list resolve from here. */
  readonly state: () => FileMenuState;
  readonly cancelWaiting: () => void;
  /** The status voice, for the sentences the menu itself owns (a cancel, a refused reopen). */
  readonly note: (line: string, reveal: boolean) => void;
}

export interface MountedFileMenu {
  isOpen(): boolean;
  dispose(): void;
}

interface OpenPickerWindow {
  showOpenFilePicker?: (options: {
    multiple: false;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<FileHandleLike[]>;
}

type PickOutcome =
  | {
      readonly kind: 'picked';
      readonly file: File;
      readonly handle: FileHandleLike | null;
      /** The load-order ticket, reserved when the choice became final. */
      readonly seq: number;
    }
  /** The dialog was dismissed — no ticket was taken. */
  | { readonly kind: 'cancelled' }
  /** The choice was final (a ticket taken) but the file could not be read. */
  | { readonly kind: 'unreadable'; readonly seq: number };

const KINDS: Record<
  MenuFileKind,
  {
    readonly label: string;
    readonly description: string;
    readonly accept: Record<string, string[]>;
    readonly inputAccept: string;
  }
> = {
  character: {
    label: 'Open character…',
    description: 'Character — glTF binary, VRM or FBX',
    accept: { 'model/gltf-binary': ['.glb', '.vrm'], 'application/octet-stream': ['.fbx'] },
    inputAccept: '.glb,.vrm,.fbx',
  },
  animation: {
    label: 'Add animation…',
    description: 'Animation — FBX, or an animation-only GLB',
    accept: { 'application/octet-stream': ['.fbx', '.glb'] },
    inputAccept: '.fbx,.glb',
  },
  document: {
    label: 'Open Document…',
    description: 'Animator Document',
    accept: { 'application/json': ['.json'] },
    inputAccept: '.json',
  },
};

const CHROME_BUTTON =
  'font: inherit; padding: 4px 10px; border-radius: 6px; cursor: pointer; ' +
  'background: var(--accent-soft); color: var(--accent-text); ' +
  'border: 1px solid var(--accent-border);';

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Ask the browser for one file of a kind. The picker comes first because its
 * handle is what Recent and relink-by-handle are made of; the input is the
 * path where the picker is absent (Firefox, Safari — and the smoke, which
 * removes the picker because a native dialog cannot be driven headless).
 * MUST be called synchronously inside a click: both need transient activation.
 * The order ticket is reserved the instant the choice is final, before the
 * picker path's read.
 */
function pickWithInput(kind: MenuFileKind, reserve: () => number): Promise<PickOutcome> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = KINDS[kind].inputAccept;
    input.dataset['fileMenuInput'] = kind;
    input.style.cssText =
      'position: fixed; width: 1px; height: 1px; opacity: 0; pointer-events: none;';
    const done = (file: File | null): void => {
      input.remove();
      resolve(
        file === null
          ? { kind: 'cancelled' }
          : { kind: 'picked', file, handle: null, seq: reserve() },
      );
    };
    input.addEventListener('change', () => done(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => done(null));
    document.body.append(input);
    input.click();
  });
}

async function pick(kind: MenuFileKind, reserve: () => number): Promise<PickOutcome> {
  const picker = (window as OpenPickerWindow).showOpenFilePicker;
  if (picker !== undefined) {
    let handle: FileHandleLike | undefined;
    try {
      [handle] = await picker({
        multiple: false,
        types: [{ description: KINDS[kind].description, accept: KINDS[kind].accept }],
      });
    } catch (err) {
      // Cancelling is not a refusal. Anything else is: say why, and try the
      // input — which may itself be refused for want of activation after the
      // await; the person then sees "cancelled" and can try again.
      if (err instanceof DOMException && err.name === 'AbortError') return { kind: 'cancelled' };
      console.warn('[animator-v3] the file picker refused — using the file input instead', err);
      return pickWithInput(kind, reserve);
    }
    if (handle === undefined) return { kind: 'cancelled' };
    // The choice is final HERE. The read below can be slow, and a later choice
    // must not overtake this one because its bytes arrived first.
    const seq = reserve();
    try {
      return { kind: 'picked', file: await handle.getFile(), handle, seq };
    } catch (err) {
      // The ticket is spent on nothing; a load in flight under an earlier
      // ticket is superseded by this failure — rare (the file vanished between
      // the dialog and the read), and said in the console. The ticket rides
      // out so the caller can tell whether its notice is still current.
      console.warn('[animator-v3] the chosen file could not be read', err);
      return { kind: 'unreadable', seq };
    }
  }
  return pickWithInput(kind, reserve);
}

export function mountFileMenu(bar: HTMLElement, deps: FileMenuDeps): MountedFileMenu {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Open ▾';
  btn.dataset['fileMenu'] = '';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', 'Open — characters, animations, Documents');
  btn.style.cssText = CHROME_BUTTON;
  const tip = attachTip(
    btn,
    'Open a character or an animation, open or save a Document, reopen a recent file',
  );

  const pop = document.createElement('div');
  pop.id = 'file-menu-pop';
  pop.className = 'file-menu-pop';
  pop.setAttribute('role', 'menu');
  pop.setAttribute('aria-label', 'Open');
  pop.dataset['open'] = 'false';
  btn.setAttribute('aria-controls', pop.id);

  const divider = document.createElement('span');
  divider.style.cssText = 'width: 1px; height: 18px; background: var(--accent-border);';
  bar.prepend(btn, tip, divider);
  document.body.append(pop);

  let open = false;

  const entry = (
    id: string,
    label: string,
    detail: string | null,
    enabled: boolean,
    reason: string | null,
    act: (() => void) | null,
  ): HTMLButtonElement => {
    const el = document.createElement('button');
    el.type = 'button';
    el.setAttribute('role', 'menuitem');
    el.dataset['menuEntry'] = id;
    const main = document.createElement('span');
    main.className = 'file-menu-label';
    main.textContent = label;
    el.append(main);
    const sub = enabled ? detail : reason;
    if (sub !== null) {
      const s = document.createElement('span');
      s.className = 'file-menu-reason';
      s.textContent = sub;
      el.append(s);
    }
    if (!enabled) {
      el.disabled = true;
      el.setAttribute('aria-disabled', 'true');
    } else if (act !== null) {
      el.addEventListener('click', act);
    }
    pop.append(el);
    return el;
  };
  const head = (text: string): void => {
    const h = document.createElement('div');
    h.className = 'file-menu-head';
    h.setAttribute('role', 'presentation');
    h.textContent = text;
    pop.append(h);
  };
  const rule = (): void => {
    const r = document.createElement('div');
    r.className = 'file-menu-rule';
    r.setAttribute('role', 'separator');
    pop.append(r);
  };

  const items = (): HTMLButtonElement[] => [
    ...pop.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'),
  ];

  const onOutside = (ev: PointerEvent): void => {
    const t = ev.target;
    if (t instanceof Node && (pop.contains(t) || btn.contains(t))) return;
    close();
  };

  const close = (): void => {
    if (!open) return;
    open = false;
    pop.dataset['open'] = 'false';
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onOutside, true);
  };

  /** Read a granted handle under a fresh ticket and hand the file on — and say how it went. */
  const openGranted = async (
    kind: MenuFileKind,
    handle: FileHandleLike,
    relinkOf?: string,
  ): Promise<'accepted' | 'refused' | 'unreadable'> => {
    const seq = deps.reserveLoad();
    let file: File;
    try {
      file = await handle.getFile();
    } catch (err) {
      console.warn('[animator-v3] a remembered file could not be read (moved?)', err);
      return 'unreadable';
    }
    return (await deps.opened(kind, file, handle, seq, relinkOf)) ? 'accepted' : 'refused';
  };

  // Each action closes the menu FIRST and then asks — the picker is modal and
  // a menu left open behind it reads as a hang.
  const act = async (kind: MenuFileKind): Promise<void> => {
    close();
    const picked = await pick(kind, deps.reserveLoad);
    if (picked.kind === 'cancelled') {
      deps.note(`${KINDS[kind].label.replace('…', '')} cancelled — nothing changed`, false);
      return;
    }
    if (picked.kind === 'unreadable') {
      // Under a stale ticket the line belongs to the newer load (Codex P2,
      // #140 round 9); the console already has the why.
      if (deps.isCurrent(picked.seq)) {
        deps.note(
          `${KINDS[kind].label.replace('…', '')} — the chosen file could not be read; nothing changed`,
          true,
        );
      }
      return;
    }
    await deps.opened(kind, picked.file, picked.handle, picked.seq);
  };
  const reopen = async (e: RememberedFile): Promise<void> => {
    close();
    const handle = await deps.grantedHandle(e.uri);
    // A refusal spoke for itself at the door; only "no way to read it" is the menu's to say.
    if (handle === null || (await openGranted(e.kind, handle)) === 'unreadable') {
      deps.note(
        `${e.name} could not be reopened — the browser no longer grants that file, or it moved; use ${KINDS[e.kind].label}`,
        true,
      );
    }
  };
  // D6: the remembered handle is tried first, then a picker of the right kind.
  // Either way the file goes through `opened`, whose door offers it to the wait.
  const relink = async (m: MissingUpload): Promise<void> => {
    close();
    // The section is a snapshot from when the menu opened; the wait may have
    // completed meanwhile by the other gesture (Codex P1, #140 round 9). A
    // relink with no wait behind it would be an ordinary import — a NEW
    // lineage over the Document that just opened — so it is refused here.
    const still = deps.state().waiting;
    if (still === null || !still.missing.some((x) => x.uri === m.uri)) {
      deps.note(`nothing to relink — "${m.name}" is no longer waited for`, true);
      return;
    }
    const handle = await deps.grantedHandle(m.uri);
    // A remembered handle that reads but is REFUSED (the wrong bytes under the
    // right name) falls through to the picker like an unreadable one (Codex
    // P2, #140 round 4) — otherwise every click retried the same file.
    // The wait is named all the way to the door (Codex P1, #140 round 10): the
    // check above can pass and the wait still end during the awaits below —
    // a drop commits, the person abandons — and then the door refuses the
    // file rather than importing it as a stranger.
    if (handle !== null && (await openGranted(m.kind, handle, still.id)) === 'accepted') return;
    const picked = await pick(m.kind, deps.reserveLoad);
    if (picked.kind !== 'picked') {
      if (picked.kind === 'cancelled' || deps.isCurrent(picked.seq)) {
        deps.note('relink cancelled — the Document still waits', false);
      }
      return;
    }
    await deps.opened(m.kind, picked.file, picked.handle, picked.seq, still.id);
  };

  const render = async (): Promise<void> => {
    pop.replaceChildren();
    const st = deps.state();
    entry(
      'open-character',
      KINDS.character.label,
      '.glb, .vrm or .fbx — a new subject; the animation in the room carries',
      true,
      null,
      () => void act('character'),
    );
    entry(
      'add-animation',
      KINDS.animation.label,
      '.fbx, or an animation-only .glb — what the subject wears',
      st.hasSubject,
      'open a character first',
      () => void act('animation'),
    );
    entry(
      'open-document',
      KINDS.document.label,
      '.harry.json — a saved session: its decisions, and the files it names',
      true,
      null,
      () => void act('document'),
    );
    entry(
      'save-document',
      'Save Document…',
      'the Document as JSON — decisions only, no bytes embedded',
      true,
      null,
      () => {
        close();
        void deps.saveDocument();
      },
    );
    rule();
    head('Recent');
    if (!st.recent.available) {
      entry('recent-none', 'nothing to reopen', null, false, st.recent.reason, null);
    } else {
      const entries = await deps.recent();
      if (entries.length === 0) {
        entry(
          'recent-none',
          'nothing to reopen',
          null,
          false,
          'files chosen through this menu are remembered by handle; dropped files are not',
          null,
        );
      }
      for (const e of entries) {
        const el = entry(
          `recent:${e.uri}`,
          e.name,
          `${e.kind} · ${when(e.at)}`,
          true,
          null,
          () => void reopen(e),
        );
        el.dataset['menuRecent'] = e.uri;
      }
    }
    if (st.waiting !== null) {
      rule();
      head(`"${st.waiting.name}" waits for`);
      for (const m of st.waiting.missing) {
        const el = entry(
          `relink:${m.uri}`,
          `relink ${m.name}`,
          `${m.kind} — a remembered handle first, then a picker`,
          true,
          null,
          () => void relink(m),
        );
        el.dataset['menuRelink'] = m.uri;
      }
      entry('abandon-waiting', 'abandon the wait', 'what is on stage stays', true, null, () => {
        close();
        deps.cancelWaiting();
      });
    }
  };

  const openMenu = async (): Promise<void> => {
    if (open) return;
    open = true;
    const r = btn.getBoundingClientRect();
    pop.style.top = `${r.bottom + 6}px`;
    pop.style.left = `${r.left}px`;
    pop.dataset['open'] = 'true';
    btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onOutside, true);
    await render();
    if (open) items()[0]?.focus();
  };

  btn.addEventListener('click', () => {
    if (open) close();
    else void openMenu();
  });
  btn.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      void openMenu();
    } else if (e.key === 'Escape') {
      close();
    }
  });
  pop.addEventListener('keydown', (e) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      btn.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      list[(at + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      list[(at - 1 + list.length) % list.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      list[list.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      close();
    }
  });

  return {
    isOpen: () => open,
    dispose() {
      close();
      btn.remove();
      tip.remove();
      divider.remove();
      pop.remove();
    },
  };
}
